// client/src/components/pages/LexiStar/Replay/ReplayMulti.tsx
//
// ST-00277 — Replay of several trucks over one window (up to MAX_REPLAY_TRUCKS), the page
// /lexistar/replay?asset_keys=k1,k2,…&from&to[&hide=…] renders below its form (./Replay).
//
//  - ONE request for every truck: GET /api/lexistar/replay/batch (the organisation's own trucks
//    only; the server skips the others and says why), normalised by ./replayMulti;
//  - ONE clock (./usePlayback) drives every truck's puck: play / pause at 1×–64×, ⏮ / ⏭ jump to
//    the previous / next event of any truck, step a minute, drag the lanes; keys as on the
//    single-truck page (Space, ← →, [ ]);
//  - the map (./ReplayMultiMap): each truck's track in its own colour, its puck in the status
//    colour of the organisation's display rules in force at that moment, its registration under
//    it; a click on a puck opens that truck's card; zones in force (ST-00272) as on one truck;
//  - the trucks list (the legend): colour, registration, status and speed at the clock, a Track
//    switch per truck and for all of them (kept in the address: `hide`), "Replay alone";
//  - the lanes (./ReplayMultiTimeline): one per truck, the status colours over time and its
//    events, one playhead;
//  - trucks without positions in the window are listed and laned as such; keys the server could
//    not replay are named above the map.
// Charts, stops, visits and deviations stay on the single-truck replay ("Replay alone").
//
// ST-00311 (up to 100 trucks): the lanes scroll inside their card past 25 trucks
// (./ReplayMultiTimeline), the trucks list scrolls inside its card on narrow screens too, and the
// note under the map sums up the trucks whose positions were capped once there are more than three.

import { type KeyboardEvent, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaBackward,
  FaEye,
  FaEyeSlash,
  FaForward,
  FaHistory,
  FaPause,
  FaPlay,
  FaStepBackward,
  FaStepForward,
} from 'react-icons/fa';
import { SegmentedControl } from '../../../../types/commonComponents';
import { EmptyState, ErrorBanner, LoadingState, cardClasses } from '../StatusBoard/atoms';
import { assetMarkerKind, type AssetMarkerKind } from '../../../map/assetMarker';
import { ASSET_STATUS, ASSET_STATUS_CLASSES, type AssetStatusKey } from '../../../map/assetStatus';
import { legendFromRules } from '../../../map/assetVisualRules';
import type { AssetDisplay, RuleVersionSpan } from '../../../map/displayRules';
import { getDisplaySpans } from '../../../../services/lexistarDisplayRules';
import ReplayMultiMap from './ReplayMultiMap';
import ReplayMultiTimeline, { type TruckLane } from './ReplayMultiTimeline';
import type { ReplayMapPosition } from './ReplayMap';
import usePlayback from './usePlayback';
import { getReplayBatch } from './replayApi';
import { displayAt, displayBands, replayDisplayLegend, type DisplayBand } from './replayDisplay';
import {
  LOW_TOLERANCE_M,
  SPEEDS,
  assetKeyOf,
  clockTime,
  nextEventTime,
  positionAt,
  prevEventTime,
  stageKey,
  toMs,
} from './replayLogic';
import {
  displayKeysOf,
  eventsBeforeOf,
  laneEventTimes,
  mergedEventTimes,
  motionWords,
  normaliseBatch,
  truckColor,
  trucksWord,
  type Basemap,
  type MultiBatch,
} from './replayMulti';
import { playButton, transportButton } from './transportStyles';

const MINUTE = 60000;
/** More trucks than this with capped positions: the note under the map counts them (ST-00311). */
const CAPPED_NAMED = 3;

/** What the asset register says of a truck (its silhouette). */
export interface MultiAssetRow {
  id?: string;
  registration: string;
  registration_normalised?: string;
  fleet_no?: string | null;
  asset_type?: string;
  vehicle_class?: string | null;
}

export interface ReplayMultiProps {
  assetKeys: string[];
  from: string;
  to: string;
  /** Trucks whose track is switched off (the address's `hide`). */
  hidden: string[];
  onHiddenChange: (keys: string[]) => void;
  /** The asset register's rows (truck or loader). */
  assets: MultiAssetRow[];
}

interface ErrorLike {
  response?: { status?: number; data?: { error?: string; details?: string[] } };
  message?: string;
}

interface TruckRowProps {
  truckKey: string;
  label: string;
  fleetNo: string | null;
  color: string;
  empty: boolean;
  off: boolean;
  status: AssetStatusKey;
  statusLabel: string;
  hollow: boolean;
  /** "62 km/h", "standing", "no position at this time". */
  motion: string;
  aloneHref: string;
  onOpen: (key: string) => void;
  onToggleTrack: (key: string) => void;
}

/**
 * One truck of the list (the legend): its colour and name (a click shows it on the map with its
 * card), "Alone" (its own replay over the window), its Track switch, and its status and speed at
 * the clock. Memoised on what it shows (up to 100 rows at 15 frames a second).
 */
const TruckRow = memo(function TruckRow({
  truckKey,
  label,
  fleetNo,
  color,
  empty,
  off,
  status,
  statusLabel,
  hollow,
  motion,
  aloneHref,
  onOpen,
  onToggleTrack,
}: TruckRowProps) {
  return (
    <li className="px-4 py-2.5" data-truck-row={truckKey}>
      <div className="flex items-center gap-2 min-w-0">
        <span
          className="inline-block w-4 h-1.5 flex-shrink-0 rounded-full"
          style={{ background: color, opacity: off ? 0.35 : 1 }}
          aria-hidden="true"
        />
        <button
          type="button"
          onClick={() => onOpen(truckKey)}
          disabled={empty}
          className="flex-1 min-w-0 text-left text-sm font-semibold text-slate-900 truncate hover:underline disabled:no-underline disabled:cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
          title={empty ? undefined : `Show ${label} on the map`}
        >
          {label}
        </button>
        <Link
          to={aloneHref}
          className="flex-shrink-0 text-[11px] font-medium text-primary-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
          aria-label={`Replay ${label} alone`}
          title="Its own replay over this window: charts, stops, visits, deviations"
        >
          Alone
        </Link>
        <button
          type="button"
          onClick={() => onToggleTrack(truckKey)}
          aria-pressed={!off}
          aria-label={`${label} track`}
          title={off ? 'Show its track' : 'Hide its track'}
          disabled={empty}
          className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium transition-colors disabled:opacity-40 disabled:cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
            off ? 'bg-slate-100 text-slate-500 border-transparent hover:text-slate-700' : 'bg-surface text-slate-700 border-slate-300'
          }`}
        >
          {off ? <FaEyeSlash size={10} aria-hidden="true" /> : <FaEye size={10} aria-hidden="true" />}
          Track
        </button>
      </div>
      <div className="mt-1 ml-6 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-600">
        {fleetNo && <span className="text-slate-500">{fleetNo}</span>}
        {empty ? (
          <span className="italic text-slate-500">No positions in this window</span>
        ) : (
          <>
            <span className="inline-flex items-center gap-1" data-status-key={status}>
              <span
                className={`inline-block w-2 h-2 rounded-full ${ASSET_STATUS_CLASSES[status].dot} ${hollow ? 'opacity-40' : ''}`}
                aria-hidden="true"
              />
              {statusLabel}
            </span>
            <span className="tabular-nums">{motion}</span>
          </>
        )}
      </div>
    </li>
  );
});

export default function ReplayMulti({ assetKeys, from, to, hidden, onHiddenChange, assets }: ReplayMultiProps) {
  const [batch, setBatch] = useState<MultiBatch | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [basemap, setBasemap] = useState<Basemap>('street');
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [focus, setFocus] = useState<{ key: string; n: number } | null>(null);
  const keysParam = assetKeys.join(',');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setOpenKey(null);
    getReplayBatch({ asset_keys: keysParam.split(','), from, to, tolerance_m: LOW_TOLERANCE_M })
      .then((res) => {
        if (alive) setBatch(normaliseBatch(res));
      })
      .catch((err: ErrorLike) => {
        if (!alive) return;
        setBatch(null);
        const d = err.response?.data;
        setError(
          d?.details?.length
            ? d.details.join(' ')
            : d?.error || err.message || 'Failed to load the replay.'
        );
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [keysParam, from, to]);

  const trucks = useMemo(() => batch?.trucks || [], [batch]);
  const win = useMemo(() => ({ start: toMs(from), end: toMs(to) }), [from, to]);
  const bounds = batch !== null && trucks.length > 0 && win.end > win.start ? win : null;
  const { cursor, playing, speed, setSpeed, seek, toggle, pause, scrubStart, scrubEnd } = usePlayback(bounds);

  // ST-00276: the display rule versions in force over the window, and every truck's events
  // before it within the rules' look-back (one call: the versions endpoint takes 100 keys).
  const [spans, setSpans] = useState<RuleVersionSpan[] | null>(null);
  const [eventsBefore, setEventsBefore] = useState<Array<{ asset_key?: string; type: string; at: string }>>([]);
  const displayKeys = useMemo(() => displayKeysOf(trucks).join(','), [trucks]);
  useEffect(() => {
    if (!bounds) {
      setSpans(null);
      setEventsBefore([]);
      return undefined;
    }
    let alive = true;
    const fromMs = Math.max(bounds.start, bounds.end - 31 * 86400000);
    getDisplaySpans(new Date(fromMs).toISOString(), new Date(bounds.end).toISOString(), {
      asset_keys: displayKeys ? displayKeys.split(',') : [],
    })
      .then((res) => {
        if (!alive) return;
        setSpans(Array.isArray(res?.spans) ? res.spans : null);
        setEventsBefore(Array.isArray(res?.events_before) ? res.events_before : []);
      })
      .catch(() => {
        if (!alive) return;
        setSpans(null);
        setEventsBefore([]);
      });
    return () => {
      alive = false;
    };
  }, [bounds, displayKeys]);

  const bandsByKey = useMemo(() => {
    const m = new Map<string, DisplayBand[]>();
    for (const t of trucks) m.set(t.key, displayBands(t.replay, spans, eventsBeforeOf(eventsBefore, t)));
    return m;
  }, [trucks, spans, eventsBefore]);
  const displayLegend = useMemo(() => replayDisplayLegend(spans), [spans]);

  // Every truck at the clock: a pure function of its track and the cursor (ST-00245).
  const positions = useMemo(() => {
    const m = new Map<string, ReplayMapPosition | null>();
    for (const t of trucks) {
      const p = cursor === null ? null : positionAt(t.replay.track, cursor);
      if (!p || cursor === null) {
        m.set(t.key, null);
        continue;
      }
      const band = t.replay.stage_bands.find((b) => b.from <= cursor && cursor <= b.to);
      m.set(t.key, { ...p, stage: band?.stage || null });
    }
    return m;
  }, [trucks, cursor]);
  const displays = useMemo(() => {
    const m = new Map<string, AssetDisplay | null>();
    for (const t of trucks) m.set(t.key, cursor === null ? null : displayAt(bandsByKey.get(t.key) || [], cursor));
    return m;
  }, [trucks, bandsByKey, cursor]);

  const kinds = useMemo(() => {
    const m = new Map<string, AssetMarkerKind>();
    for (const t of trucks) {
      const row = assets.find((a) => assetKeyOf(a) === t.key || (!!t.replay.asset?.id && a.id === t.replay.asset.id));
      m.set(t.key, assetMarkerKind(t.replay.asset, row));
    }
    return m;
  }, [trucks, assets]);

  // The puck colours this replay uses, in the scheme's order (the map legend).
  const statusKeys = useMemo(() => {
    const present = new Set<AssetStatusKey>();
    for (const bands of bandsByKey.values()) for (const b of bands) present.add(b.display.base.token as AssetStatusKey);
    return legendFromRules({}, { keys: [...present], overlayIds: [] })
      .map((e) => e.key)
      .filter((k): k is AssetStatusKey => !!k);
  }, [bandsByKey]);

  const hiddenSet = useMemo(() => new Set(hidden), [hidden]);
  const toggleTrack = useCallback(
    (key: string) => onHiddenChange(hiddenSet.has(key) ? hidden.filter((k) => k !== key) : [...hidden, key]),
    [hidden, hiddenSet, onHiddenChange]
  );
  // "All tracks": the trucks that have one (a truck without positions has no line to switch).
  const withTrack = trucks.filter((t) => !t.empty);
  const allHidden = withTrack.length > 0 && withTrack.every((t) => hiddenSet.has(t.key));
  const setAllTracks = (show: boolean) => onHiddenChange(show ? [] : withTrack.map((t) => t.key));

  const aloneHref = useCallback(
    (key: string) =>
      `/lexistar/replay?${new URLSearchParams({ asset_key: key, from, to }).toString()}`,
    [from, to]
  );

  // ── Transport ──
  const events = useMemo(() => mergedEventTimes(trucks), [trucks]);
  const goPrevEvent = () => {
    if (cursor === null) return;
    const t = prevEventTime(events, cursor);
    if (t !== null) seek(t);
  };
  const goNextEvent = () => {
    if (cursor === null) return;
    const t = nextEventTime(events, cursor);
    if (t !== null) seek(t);
  };
  const step = (dir: number) => {
    if (cursor === null) return;
    pause();
    seek(cursor + dir * MINUTE);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!bounds) return;
    const target = e.target as HTMLElement | null;
    const tag = target?.tagName ?? '';
    if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'].includes(tag) && e.key === ' ') return;
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || target?.isContentEditable) return;
    if (target?.getAttribute?.('role') === 'slider') return;
    if (e.key === ' ') {
      e.preventDefault();
      toggle();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      step(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      step(1);
    } else if (e.key === '[') {
      goPrevEvent();
    } else if (e.key === ']') {
      goNextEvent();
    }
  };

  const openFromList = useCallback((key: string) => {
    setOpenKey(key);
    setFocus((f) => ({ key, n: (f?.n || 0) + 1 }));
  }, []);

  const lanes: TruckLane[] = useMemo(
    () =>
      trucks.map((t) => ({
        key: t.key,
        label: t.label,
        color: truckColor(t.index, basemap),
        bands: bandsByKey.get(t.key) || [],
        events: laneEventTimes(t.replay.events, win.start, win.end),
        dim: hiddenSet.has(t.key),
        empty: t.empty,
      })),
    [trucks, basemap, bandsByKey, win.start, win.end, hiddenSet]
  );

  if (loading && !batch) return <LoadingState label={`Loading ${trucksWord(assetKeys.length)}...`} />;

  const skipped = batch?.skipped || [];
  const withData = trucks.filter((t) => !t.empty);
  const moving = trucks.filter((t) => positions.get(t.key)?.motion === 'moving').length;
  const standing = trucks.filter((t) => positions.get(t.key)?.motion === 'stationary').length;
  const coarser = trucks.filter((t) => t.toleranceM !== null && t.toleranceM > LOW_TOLERANCE_M);
  const maxTolerance = Math.max(LOW_TOLERANCE_M, ...coarser.map((t) => t.toleranceM as number));
  const capped = trucks.filter((t) => t.truncated);
  const firstCap = Math.min(...capped.map((t) => t.nextFrom ?? Infinity));

  return (
    <div className="space-y-4" onKeyDown={onKeyDown}>
      <ErrorBanner message={error} onDismiss={() => setError(null)} />

      {skipped.length > 0 && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          role="status"
          data-testid="replay-multi-skipped"
        >
          <span className="font-semibold">
            {skipped.length === 1 ? '1 truck is not replayed' : `${skipped.length} trucks are not replayed`}:
          </span>{' '}
          {skipped.map((s, i) => (
            <span key={s.key}>
              {i > 0 && '; '}
              <span className="font-medium">{s.label}</span> ({s.message})
            </span>
          ))}
          .
        </div>
      )}

      {batch && trucks.length === 0 && !error && (
        <div className={cardClasses}>
          <EmptyState icon={FaHistory} title="Nothing to replay">
            None of these trucks could be found, or they are not your organisation’s. Choose your own trucks above.
          </EmptyState>
        </div>
      )}

      {batch && trucks.length > 0 && withData.length === 0 && (
        <div className={cardClasses}>
          <EmptyState icon={FaHistory} title="No positions in this window">
            None of these {trucks.length} trucks reported a position between these times. Widen the window or
            check the devices’ health.
          </EmptyState>
        </div>
      )}

      {batch && bounds && withData.length > 0 && cursor !== null && (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
          <div className="xl:col-span-9 space-y-4 min-w-0">
            <ReplayMultiMap
              trucks={trucks}
              kinds={kinds}
              positions={positions}
              displays={displays}
              hidden={hiddenSet}
              basemap={basemap}
              onBasemapChange={setBasemap}
              openKey={openKey}
              onOpenChange={setOpenKey}
              window={win}
              cursor={cursor}
              fitKey={`${keysParam}|${from}|${to}`}
              focus={focus}
              aloneHref={aloneHref}
              statusKeys={statusKeys}
              displayLegend={displayLegend}
            />
            <p className="text-[11px] text-slate-500" role="status" data-testid="replay-multi-status">
              {coarser.length > 0
                ? `Simplified tracks (${LOW_TOLERANCE_M}–${maxTolerance} m: ${trucksWord(coarser.length)} drawn coarser to fit ${trucksWord(trucks.length)} in one replay).`
                : `Simplified tracks (${LOW_TOLERANCE_M} m).`}{' '}
              {`${batch.points.returned.toLocaleString('en-GB')} of ${batch.points.read.toLocaleString('en-GB')} fixes drawn.`}{' '}
              Open a truck alone for every fix.
              {capped.length > 0 && capped.length <= CAPPED_NAMED && (
                <span className="block text-amber-700 font-medium">
                  {capped.map((t) => `${t.label}: positions capped at ${clockTime(t.nextFrom, false)}`).join('; ')} —
                  shorten the time range or replay the truck alone.
                </span>
              )}
              {capped.length > CAPPED_NAMED && (
                <span className="block text-amber-700 font-medium" data-testid="replay-multi-capped">
                  {`${trucksWord(capped.length)}: positions capped${
                    Number.isFinite(firstCap) ? ` from ${clockTime(firstCap, false)}` : ''
                  } — shorten the time range, choose fewer trucks or replay a truck alone.`}
                </span>
              )}
            </p>

            <section className={`${cardClasses} p-4 space-y-3`} aria-label="Playback">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1.5" role="group" aria-label="Transport">
                  <button type="button" onClick={goPrevEvent} className={transportButton} aria-label="Previous event" title="Previous event ( [ )">
                    <FaStepBackward size={12} />
                  </button>
                  <button type="button" onClick={() => step(-1)} className={transportButton} aria-label="Step back a minute" title="Step back a minute ( ← )">
                    <FaBackward size={12} />
                  </button>
                  <button type="button" onClick={toggle} className={playButton} aria-label={playing ? 'Pause' : 'Play'} title="Play / pause (Space)">
                    {playing ? <FaPause size={13} /> : <FaPlay size={13} />}
                  </button>
                  <button type="button" onClick={() => step(1)} className={transportButton} aria-label="Step forward a minute" title="Step forward a minute ( → )">
                    <FaForward size={12} />
                  </button>
                  <button type="button" onClick={goNextEvent} className={transportButton} aria-label="Next event" title="Next event ( ] )">
                    <FaStepForward size={12} />
                  </button>
                </div>
                <div className="w-full sm:w-auto">
                  <SegmentedControl
                    options={SPEEDS.map((s) => ({ value: s, label: `${s}×` }))}
                    value={speed}
                    onChange={setSpeed}
                    ariaLabel="Playback speed"
                  />
                </div>
                <div className="ml-auto w-56 text-right" aria-live="off">
                  <p className="text-lg font-bold tabular-nums text-slate-900">{clockTime(cursor)}</p>
                  <p className="text-xs font-medium text-slate-700 tabular-nums truncate" data-testid="replay-readout">
                    {`${moving} of ${trucksWord(trucks.length)} moving`}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {`${standing} standing · ${trucks.length - moving - standing} without a position now`}
                  </p>
                </div>
              </div>
              <ReplayMultiTimeline
                start={bounds.start}
                end={bounds.end}
                cursor={cursor}
                onSeek={seek}
                onScrubStart={scrubStart}
                onScrubEnd={scrubEnd}
                lanes={lanes}
              />
            </section>
          </div>

          <aside
            className={`${cardClasses} xl:col-span-3 overflow-hidden flex flex-col max-h-[40rem] xl:max-h-[calc(100vh-120px)] xl:sticky xl:top-20`}
            aria-label="Trucks in this replay"
          >
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-200">
              <h2 className="text-sm font-semibold text-slate-900">
                Trucks <span className="text-slate-500 font-normal tabular-nums">({trucks.length})</span>
              </h2>
              <button
                type="button"
                onClick={() => setAllTracks(allHidden)}
                className="text-xs font-medium text-primary-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
              >
                {allHidden ? 'Show all tracks' : 'Hide all tracks'}
              </button>
            </div>
            <ul className="flex-1 overflow-y-auto divide-y divide-slate-100" aria-label="Truck legend">
              {trucks.map((t) => {
                const p = positions.get(t.key) || null;
                const d = displays.get(t.key) || null;
                const status: AssetStatusKey = (d?.base.token as AssetStatusKey | undefined) ?? stageKey(p?.stage);
                return (
                  <TruckRow
                    key={t.key}
                    truckKey={t.key}
                    label={t.label}
                    fleetNo={t.fleetNo}
                    color={truckColor(t.index, basemap)}
                    empty={t.empty}
                    off={hiddenSet.has(t.key)}
                    status={status}
                    statusLabel={d?.base.label || ASSET_STATUS[status].label}
                    hollow={!p || p.motion === 'before' || p.motion === 'after'}
                    motion={
                      p && p.motion === 'moving' && p.speed_kmh != null ? `${Math.round(p.speed_kmh)} km/h` : motionWords(p)
                    }
                    aloneHref={aloneHref(t.key)}
                    onOpen={openFromList}
                    onToggleTrack={toggleTrack}
                  />
                );
              })}
            </ul>
            <p className="px-4 py-2.5 border-t border-slate-200 text-[11px] text-slate-500">
              Charts, stops, visits and deviations are per truck: open one with <span className="font-medium">Alone</span>.
            </p>
          </aside>
        </div>
      )}
    </div>
  );
}
